import React, { useState } from 'react';
import * as S from './AdvancesStyles';

const CATS = ['Sugar', 'Medicine', 'Labour', 'Transport', 'Maintenance', 'Other'];
const CAT_COLOR = {
  Sugar: '#EA5B2A',
  Medicine: '#2F6FED',
  Labour: '#1E9E5A',
  Transport: '#8B5CE0',
  Maintenance: '#B9791A',
  Other: '#6B7280'
};

const fmt = n => (n < 0 ? '−₹' : '₹') + Math.round(Math.abs(n)).toLocaleString('en-IN');
const sum = (arr, f) => arr.reduce((s, x) => s + f(x), 0);

/* --- High-Fidelity Interactive SVG Donut Chart with Hover Tooltip --- */
const DonutChartSVG = ({ data, labels, colors }) => {
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, title: '', value: 0 });

  const total = data.reduce((a, b) => a + b, 0);
  if (total <= 0) {
    return (
      <div style={{ padding: '30px', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>
        No spend data recorded yet.
      </div>
    );
  }

  let cumulativeAngle = 0;
  const radius = 65;
  const strokeWidth = 26;
  const center = 80;
  const circumference = 2 * Math.PI * radius;

  const slices = data.map((val, idx) => {
    const percentage = val / total;
    const strokeDasharray = `${percentage * circumference} ${circumference}`;
    const strokeDashoffset = -cumulativeAngle * circumference;
    cumulativeAngle += percentage;
    return {
      label: labels[idx],
      val,
      color: colors[labels[idx]] || '#6B7280',
      strokeDasharray,
      strokeDashoffset
    };
  });

  const handleMouseMove = (e, slice) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      show: true,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 40,
      title: slice.label,
      value: slice.val
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'center', position: 'relative' }}>
      {tooltip.show && (
        <div
          style={{
            position: 'absolute',
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            background: '#181A20',
            color: '#fff',
            padding: '5px 10px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: 700,
            pointerEvents: 'none',
            zIndex: 10,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            transform: 'translateX(-50%)'
          }}
        >
          {tooltip.title}: {fmt(tooltip.value)}
        </div>
      )}

      <svg
        width="160"
        height="160"
        viewBox="0 0 160 160"
        onMouseLeave={() => setTooltip({ ...tooltip, show: false })}
      >
        {slices.map((slice, idx) => (
          <circle
            key={idx}
            cx={center}
            cy={center}
            r={radius}
            fill="transparent"
            stroke={slice.color}
            strokeWidth={strokeWidth}
            strokeDasharray={slice.strokeDasharray}
            strokeDashoffset={slice.strokeDashoffset}
            transform={`rotate(-90 ${center} ${center})`}
            style={{ cursor: 'pointer', transition: 'stroke-width 0.2s ease' }}
            onMouseMove={e => handleMouseMove(e, slice)}
            onMouseEnter={e => handleMouseMove(e, slice)}
          />
        ))}
        <text
          x={center}
          y={center - 4}
          textAnchor="middle"
          fontSize="14"
          fontWeight="800"
          fill="#1E2130"
        >
          {fmt(total)}
        </text>
        <text
          x={center}
          y={center + 14}
          textAnchor="middle"
          fontSize="10"
          fontWeight="600"
          fill="#6B7280"
        >
          Total Spend
        </text>
      </svg>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px', marginTop: '12px', justifyContent: 'center' }}>
        {slices.map((slice, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#6B7280' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: slice.color }} />
            <span style={{ fontWeight: 600, color: '#1E2130' }}>{slice.label}:</span> {fmt(slice.val)}
          </div>
        ))}
      </div>
    </div>
  );
};

/* --- High-Fidelity SVG Area Line Chart with Hover Tooltip --- */
const AreaLineChartSVG = ({ labels, data }) => {
  const [activePoint, setActivePoint] = useState(null);
  const maxVal = Math.max(...data, 1000);
  const width = 340;
  const height = 160;
  const padding = 25;

  const points = data.map((val, idx) => {
    const x = padding + (idx / (data.length - 1)) * (width - 2 * padding);
    const y = height - padding - (val / maxVal) * (height - 2 * padding);
    return { x, y, val, label: labels[idx] };
  });

  const pathD = points.reduce(
    (acc, p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`),
    ''
  );

  const areaD = `${pathD} L ${points[points.length - 1].x} ${height - padding} L ${points[0].x} ${height - padding} Z`;

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', position: 'relative' }}>
      <svg width="100%" height="200" viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#EA5B2A" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#EA5B2A" stopOpacity="0.0" />
          </linearGradient>
        </defs>

        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#E7E8EC" strokeWidth="1" />
        <line x1={padding} y1={padding} x2={width - padding} y2={padding} stroke="#E7E8EC" strokeDasharray="3 3" strokeWidth="1" />

        <path d={areaD} fill="url(#lineGrad)" />
        <path d={pathD} fill="none" stroke="#EA5B2A" strokeWidth="3" strokeLinecap="round" />

        {points.map((p, idx) => (
          <g
            key={idx}
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => setActivePoint(p)}
            onMouseLeave={() => setActivePoint(null)}
          >
            <circle cx={p.x} cy={p.y} r={activePoint?.label === p.label ? "6" : "4"} fill="#EA5B2A" stroke="#fff" strokeWidth="2" />
            <text x={p.x} y={height - 8} textAnchor="middle" fontSize="10" fontWeight="600" fill="#6B7280">
              {labels[idx]}
            </text>
            <text x={p.x} y={p.y - 10} textAnchor="middle" fontSize="9" fontWeight="700" fill="#1E2130">
              {fmt(p.val)}
            </text>

            {activePoint?.label === p.label && (
              <g>
                <rect
                  x={p.x - 35}
                  y={p.y - 32}
                  width="70"
                  height="20"
                  rx="4"
                  fill="#181A20"
                />
                <text
                  x={p.x}
                  y={p.y - 18}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="700"
                  fill="#ffffff"
                >
                  {fmt(p.val)}
                </text>
              </g>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
};

/* --- High-Fidelity SVG Horizontal Bar Chart with Hover Tooltip --- */
const HorizontalBarChartSVG = ({ data }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  if (!data || data.length === 0) {
    return (
      <div style={{ padding: '30px', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>
        No expenses recorded yet.
      </div>
    );
  }
  const maxVal = Math.max(...data.map(x => x.val), 1000);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '10px 0', position: 'relative' }}>
      {data.map((item, idx) => {
        const pct = Math.min((item.val / maxVal) * 100, 100);
        const isHovered = hoveredIdx === idx;
        return (
          <div
            key={idx}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', cursor: 'pointer' }}
            onMouseEnter={() => setHoveredIdx(idx)}
            onMouseLeave={() => setHoveredIdx(null)}
          >
            <span style={{ width: '80px', fontWeight: 600, color: '#1E2130', textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.name}
            </span>
            <div style={{ flex: 1, background: '#F4F5F7', borderRadius: '6px', height: '14px', overflow: 'hidden', position: 'relative' }}>
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: isHovered ? '#1976d2' : '#2F6FED',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease'
                }}
              />
            </div>
            <span style={{ width: '65px', fontWeight: 700, color: '#1E2130' }}>{fmt(item.val)}</span>

            {isHovered && (
              <div
                style={{
                  position: 'absolute',
                  right: '10px',
                  marginTop: '-30px',
                  background: '#181A20',
                  color: '#fff',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 700,
                  pointerEvents: 'none',
                  zIndex: 10,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}
              >
                {item.name}: {fmt(item.val)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const OverviewTab = ({ txns, members, memberStats, onOpenPassbook }) => {
  const outstandingAdv = sum(
    members.filter(m => m.mode === 'advance'),
    m => Math.max(memberStats(m.id).balance, 0)
  );

  const pending = txns.filter(
    t => t.type === 'expense' && t.approvalStatus === 'pending'
  ).length;

  const hasApproved = txns.some(t => t.type === 'expense' && t.approvalStatus === 'approved');

  const payable =
    sum(
      members.filter(m => m.mode === 'reimbursement'),
      m => memberStats(m.id).unpaid
    ) +
    sum(
      members.filter(m => m.mode === 'advance'),
      m => memberStats(m.id).converted
    );

  // Current Month Spend
  const nowStr = new Date().toISOString().slice(0, 7);
  const thisMonth = sum(
    txns.filter(
      t =>
        t.type === 'expense' &&
        (hasApproved ? t.approvalStatus === 'approved' : true) &&
        t.date &&
        t.date.startsWith(nowStr)
    ),
    t => t.amount
  );
  const nowLabel = new Date().toLocaleString('default', { month: 'short', year: 'numeric' });

  const balanceRows = members
    .map(m => {
      const s = memberStats(m.id);
      const owed = m.mode === 'advance' ? s.converted : s.unpaid;
      const held = m.mode === 'advance' ? s.balance : 0;
      return { m, s, owed, held };
    })
    .filter(r => r.owed > 0 || r.held > 0)
    .sort((a, b) => b.owed + b.held - (a.owed + a.held));

  // Category Totals (From all logged or approved expenses)
  const catTotals = CATS.map(c =>
    sum(
      txns.filter(
        t =>
          t.type === 'expense' &&
          (hasApproved ? t.approvalStatus === 'approved' : true) &&
          t.category === c
      ),
      t => t.amount
    )
  );

  // Dynamic Month Trend — rolling 4-month window ending this month, computed from the
  // real current date instead of a hardcoded May-Aug range (which silently stopped
  // showing any data at all once the real date moved past August).
  const months = Array.from({ length: 4 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - (3 - i));
    return d.toISOString().slice(0, 7);
  });
  const monthLabels = months.map(m => new Date(m + '-02').toLocaleString('default', { month: 'short' }));
  const monthTotals = months.map(mo =>
    sum(
      txns.filter(
        t =>
          t.type === 'expense' &&
          (hasApproved ? t.approvalStatus === 'approved' : true) &&
          t.date &&
          t.date.startsWith(mo)
      ),
      t => t.amount
    )
  );

  // Top Spenders Data (Match by ID or Name, filtered to actual spenders)
  const spendByMember = members
    .map(m => {
      const memberExpenseSum = sum(
        txns.filter(
          t =>
            t.type === 'expense' &&
            (hasApproved ? t.approvalStatus === 'approved' : true) &&
            (String(t.memberId) === String(m.id) || (t.memberName && t.memberName.toLowerCase() === m.name.toLowerCase()))
        ),
        t => t.amount
      );
      return {
        name: m.name ? m.name.split(' ')[0] : 'Staff',
        fullName: m.name || 'Staff',
        val: memberExpenseSum
      };
    })
    .filter(item => item.val > 0)
    .sort((a, b) => b.val - a.val)
    .slice(0, 6);

  return (
    <div>
      <S.KpiRow>
        <S.KpiCard accent>
          <div className="lbl">Advances Outstanding</div>
          <div className="val">{fmt(outstandingAdv)}</div>
          <div className="sub">held by advance-model team</div>
        </S.KpiCard>
        <S.KpiCard>
          <div className="lbl">Reimbursement Payable</div>
          <div className="val">{fmt(payable)}</div>
          <div className="sub">approved, unpaid</div>
        </S.KpiCard>
        <S.KpiCard>
          <div className="lbl">Pending Approvals</div>
          <div className="val">{pending}</div>
          <div className="sub">need review</div>
        </S.KpiCard>
        <S.KpiCard>
          <div className="lbl">This Month's Spend</div>
          <div className="val">{fmt(thisMonth)}</div>
          <div className="sub">approved expenses, {nowLabel}</div>
        </S.KpiCard>
      </S.KpiRow>

      <S.Grid2>
        <S.CardBox>
          <h3>Spend by category</h3>
          <div className="hint">Approved expenses, all time</div>
          <DonutChartSVG data={catTotals} labels={CATS} colors={CAT_COLOR} />
        </S.CardBox>
        <S.CardBox>
          <h3>Balance status</h3>
          <div className="hint">Everyone with a live balance or unpaid payable</div>
          <div>
            {balanceRows.length ? (
              balanceRows.map(r => (
                <S.BalRow key={r.m.id}>
                  <div>
                    <span
                      className="bal-name"
                      onClick={() => onOpenPassbook(r.m.id)}
                    >
                      {r.m.name}
                    </span>
                    <span
                      className={`bal-tag ${
                        r.m.mode === 'advance' ? 'tag-advance' : 'tag-reimb'
                      }`}
                    >
                      {r.m.mode}
                    </span>
                  </div>
                  <div>
                    {r.held > 0 && <span className="amt-pos">{fmt(r.held)} held</span>}{' '}
                    {r.owed > 0 && <span className="amt-owe">{fmt(r.owed)} owed</span>}
                  </div>
                </S.BalRow>
              ))
            ) : (
              <div style={{ padding: '30px', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>
                Everyone is squared up.
              </div>
            )}
          </div>
        </S.CardBox>
      </S.Grid2>

      <S.Grid2 style={{ marginTop: '16px' }}>
        <S.CardBox>
          <h3>Monthly spend trend</h3>
          <div className="hint">Approved expenses by month</div>
          <AreaLineChartSVG labels={monthLabels} data={monthTotals} />
        </S.CardBox>
        <S.CardBox>
          <h3>Top spenders</h3>
          <div className="hint">By approved expense amount</div>
          <HorizontalBarChartSVG data={spendByMember} />
        </S.CardBox>
      </S.Grid2>
    </div>
  );
};

export default OverviewTab;
